import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface PaymentProviderRow {
  id: string;
  providerKey: string;
  displayName: string;
  isEnabled: boolean;
  capabilities: string[];
  settingsJson?: string | null;
}

export interface PaymentIntentRow {
  id: string;
  providerKey: string;
  externalReference: string;
  idempotencyKey: string;
  amount: number;
  capturedAmount: number;
  refundedAmount: number;
  currencyCode: string;
  status: string;
}

export interface WalletKeyTemplateRow {
  key: string;
  displayName: string;
  ownerType: string;
}

export interface WalletAccountRow {
  id: string;
  walletKey: string;
  currencyCode: string;
  balance: number;
  ledgerBalance?: number;
  reservedBalance: number;
  availableBalance: number;
  allowNegativeBalance: boolean;
  ownerType?: string;
}

export interface FoundationWalletAccountRow {
  id: string;
  walletKey: string;
  ownerType: string;
  customerAccountId: string | null;
  sellerId: string | null;
  storeId: string | null;
  currencyCode: string;
  status: string;
  ledgerBalance: number;
  heldBalance: number;
  availableBalance: number;
  allowNegativeBalance: boolean;
}

export interface WalletLedgerRow {
  id: string;
  walletAccountId: string;
  direction: string;
  entryType: string;
  amount: number;
  currencyCode: string;
  balanceAfter: number;
  occurredAtUtc: string;
  isPosted: boolean;
}

export interface WalletHoldRow {
  id: string;
  walletAccountId: string;
  amount: number;
  currencyCode: string;
  status: string;
  idempotencyKey: string;
}

export interface WalletTopUpRow {
  id: string;
  walletAccountId: string;
  amount: number;
  currencyCode: string;
  status: string;
  idempotencyKey: string;
}

export interface WalletPayoutRow {
  id: string;
  walletAccountId: string;
  sellerId: string | null;
  amount: number;
  currencyCode: string;
  status: string;
  idempotencyKey: string;
}

export interface WalletReconciliationSummaryRow {
  activeAccounts: number;
  frozenAccounts: number;
  totalLedgerBalance: number;
  totalHeldBalance: number;
  totalAvailableBalance: number;
  activeHolds: number;
  pendingTopUps: number;
  pendingPayouts: number;
  unpostedLedgerEntries: number;
}

export interface LoanRow {
  id: string;
  customerId: string;
  externalReference: string;
  idempotencyKey: string;
  principalAmount: number;
  annualRatePercent: number;
  termMonths: number;
  currencyCode: string;
  status: string;
}

export interface LoanRepaymentRow {
  id: string;
  loanId: string;
  idempotencyKey: string;
  amount: number;
  currencyCode: string;
  journalEntryId: string | null;
  postingErrorCode: string | null;
}

export interface AccountingCustomerRow {
  key: string;
  displayName: string;
  code?: string;
  status?: string;
}

interface PagedApiResponse<T> {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
}

type ListApiResponse<T> = T[] | PagedApiResponse<T>;

@Injectable({ providedIn: 'root' })
export class PaymentsAdminService {
  private readonly http = inject(HttpClient);
  private readonly apiBase = environment.apiBaseUrl;

  private unwrapListResponse<T>(response: ListApiResponse<T>): T[] {
    if (Array.isArray(response)) {
      return response;
    }

    return response?.items ?? [];
  }

  listProviders(): Observable<PaymentProviderRow[]> {
    return this.http.get<PaymentProviderRow[]>(`${this.apiBase}/api/v1/payments/providers`);
  }

  upsertProvider(payload: {
    providerKey: string;
    displayName: string;
    isEnabled: boolean;
    callbackSecret: string;
    settingsJson?: string | null;
  }): Observable<PaymentProviderRow> {
    return this.http.post<PaymentProviderRow>(`${this.apiBase}/api/v1/payments/providers`, payload);
  }

  listIntents(): Observable<PaymentIntentRow[]> {
    return this.http
      .get<ListApiResponse<PaymentIntentRow>>(`${this.apiBase}/api/v1/payments/intents`)
      .pipe(map(response => this.unwrapListResponse(response)));
  }

  createIntent(payload: { providerKey: string; externalReference: string; idempotencyKey: string; amount: number; currencyCode: string }): Observable<PaymentIntentRow> {
    return this.http.post<PaymentIntentRow>(`${this.apiBase}/api/v1/payments/intents`, payload);
  }

  listWalletKeyTemplates(ownerType?: string): Observable<WalletKeyTemplateRow[]> {
    const params = ownerType ? { ownerType } : undefined;
    return this.http.get<WalletKeyTemplateRow[]>(`${this.apiBase}/api/v1/wallet/foundation/wallet-key-templates`, { params });
  }

  listWalletAccounts(): Observable<WalletAccountRow[]> {
    return this.http.get<WalletAccountRow[]>(`${this.apiBase}/api/v1/wallet/accounts`);
  }

  createWalletAccount(payload: { walletKey: string; currencyCode: string; allowNegativeBalance: boolean }): Observable<WalletAccountRow> {
    return this.http.post<WalletAccountRow>(`${this.apiBase}/api/v1/wallet/accounts`, payload);
  }

  createFoundationWalletAccount(payload: {
    walletKey: string;
    currencyCode: string;
    ownerType: string;
    customerAccountId?: string | null;
    sellerId?: string | null;
    storeId?: string | null;
    ownerPartyId?: string | null;
    allowNegativeBalance: boolean;
  }): Observable<FoundationWalletAccountRow> {
    return this.http.post<FoundationWalletAccountRow>(`${this.apiBase}/api/v1/wallet/foundation/accounts`, payload);
  }

  listLedger(walletAccountId?: string): Observable<WalletLedgerRow[]> {
    const params = walletAccountId ? { walletAccountId } : undefined;
    return this.http
      .get<PagedApiResponse<WalletLedgerRow>>(`${this.apiBase}/api/v1/wallet/ledger`, { params })
      .pipe(map(response => this.unwrapListResponse(response)));
  }

  listHolds(walletAccountId?: string): Observable<WalletHoldRow[]> {
    const params = walletAccountId ? { walletAccountId } : undefined;
    return this.http.get<WalletHoldRow[]>(`${this.apiBase}/api/v1/wallet/holds`, { params });
  }

  listTopUpIntents(walletAccountId?: string): Observable<WalletTopUpRow[]> {
    const params = walletAccountId ? { walletAccountId } : undefined;
    return this.http.get<WalletTopUpRow[]>(`${this.apiBase}/api/v1/wallet/top-up-intents`, { params });
  }

  listPayoutRequests(sellerId?: string): Observable<WalletPayoutRow[]> {
    const params = sellerId ? { sellerId } : undefined;
    return this.http.get<WalletPayoutRow[]>(`${this.apiBase}/api/v1/wallet/payout-requests`, { params });
  }

  batchApprovePayoutRequests(payoutRequestIds: string[], approverReference?: string): Observable<{
    processedCount: number;
    failedCount: number;
    results: WalletPayoutRow[];
    failures: { payoutRequestId: string; error: string }[];
  }> {
    return this.http.post<{
      processedCount: number;
      failedCount: number;
      results: WalletPayoutRow[];
      failures: { payoutRequestId: string; error: string }[];
    }>(`${this.apiBase}/api/v1/wallet/payout-requests/batch/approve`, {
      payoutRequestIds,
      approverReference: approverReference ?? 'admin-wallet-batch'
    });
  }

  getReconciliationSummary(): Observable<WalletReconciliationSummaryRow> {
    return this.http.get<WalletReconciliationSummaryRow>(`${this.apiBase}/api/v1/wallet/reconciliation/summary`);
  }

  listLoans(): Observable<LoanRow[]> {
    return this.http
      .get<ListApiResponse<LoanRow>>(`${this.apiBase}/api/v1/lending/loans`)
      .pipe(map(response => this.unwrapListResponse(response)));
  }

  createLoan(payload: {
    customerId: string;
    externalReference: string;
    idempotencyKey: string;
    principalAmount: number;
    annualRatePercent: number;
    termMonths: number;
    currencyCode: string;
  }): Observable<LoanRow> {
    return this.http.post<LoanRow>(`${this.apiBase}/api/v1/lending/loans`, payload);
  }

  listRepayments(loanId?: string): Observable<LoanRepaymentRow[]> {
    if (!loanId) {
      return this.http
        .get<ListApiResponse<LoanRepaymentRow>>(`${this.apiBase}/api/v1/lending/repayments`)
        .pipe(map(response => this.unwrapListResponse(response)));
    }

    return this.http
      .get<ListApiResponse<LoanRepaymentRow>>(`${this.apiBase}/api/v1/lending/repayments`, { params: { loanId } })
      .pipe(map(response => this.unwrapListResponse(response)));
  }

  listAccountingCustomers(query?: string): Observable<AccountingCustomerRow[]> {
    if (!query?.trim()) {
      return this.http.get<AccountingCustomerRow[]>(`${this.apiBase}/api/v1/accounting/customers`);
    }

    return this.http.get<AccountingCustomerRow[]>(`${this.apiBase}/api/v1/accounting/customers`, { params: { query: query.trim() } });
  }

  listBnplPlans(storeId?: string, currencyCode?: string): Observable<BnplPlanRow[]> {
    const params: Record<string, string> = {};
    if (storeId?.trim()) params['storeId'] = storeId.trim();
    if (currencyCode?.trim()) params['currencyCode'] = currencyCode.trim();
    return this.http.get<BnplPlanRow[]>(`${this.apiBase}/api/v1/bnpl/plans`, { params });
  }

  createBnplPlan(payload: CreateBnplPlanPayload): Observable<BnplPlanRow> {
    return this.http.post<BnplPlanRow>(`${this.apiBase}/api/v1/bnpl/plans`, payload);
  }

  listCreditApplications(customerAccountId?: string, page = 1, pageSize = 25): Observable<PagedApiResponse<CreditApplicationRow>> {
    const params: Record<string, string | number> = { page, pageSize };
    if (customerAccountId?.trim()) params['customerAccountId'] = customerAccountId.trim();
    return this.http.get<PagedApiResponse<CreditApplicationRow>>(`${this.apiBase}/api/v1/credit/applications`, { params });
  }

  getCreditApplication(applicationId: string): Observable<CreditApplicationRow> {
    return this.http.get<CreditApplicationRow>(`${this.apiBase}/api/v1/credit/applications/${applicationId}`);
  }

  getCreditDecision(applicationId: string): Observable<CreditDecisionRow | null> {
    return this.http.get<CreditDecisionRow | null>(`${this.apiBase}/api/v1/credit/applications/${applicationId}/decision`);
  }

  decideCreditApplication(applicationId: string, payload: CreditDecisionPayload): Observable<CreditDecisionRow> {
    return this.http.post<CreditDecisionRow>(`${this.apiBase}/api/v1/credit/applications/${applicationId}/decide`, payload);
  }

  submitCreditApplication(payload: SubmitCreditApplicationPayload): Observable<CreditApplicationRow> {
    return this.http.post<CreditApplicationRow>(`${this.apiBase}/api/v1/credit/applications/submit`, payload);
  }

  commitBnplContract(payload: BnplCommitPayload): Observable<BnplCommitResponse> {
    return this.http.post<BnplCommitResponse>(`${this.apiBase}/api/v1/bnpl/commit`, payload);
  }

  activateLoanContract(loanContractId: string, payload: ActivateLoanContractPayload): Observable<LoanContractRow> {
    return this.http.post<LoanContractRow>(`${this.apiBase}/api/v1/loans/contracts/${loanContractId}/activate`, payload);
  }

  listCreditLines(customerAccountId?: string): Observable<CreditLineRow[]> {
    const params = customerAccountId?.trim() ? { customerAccountId: customerAccountId.trim() } : undefined;
    return this.http.get<CreditLineRow[]>(`${this.apiBase}/api/v1/credit-lines`, { params });
  }

  listLoanContracts(customerAccountId?: string): Observable<LoanContractRow[]> {
    const params = customerAccountId?.trim() ? { customerAccountId: customerAccountId.trim() } : undefined;
    return this.http.get<LoanContractRow[]>(`${this.apiBase}/api/v1/loans/contracts`, { params });
  }

  listLoanInstallments(loanContractId: string): Observable<LoanInstallmentRow[]> {
    return this.http.get<LoanInstallmentRow[]>(`${this.apiBase}/api/v1/loans/contracts/${loanContractId}/installments`);
  }

  listBnplRepayments(loanContractId?: string, page = 1, pageSize = 25): Observable<PagedApiResponse<BnplRepaymentRow>> {
    const params: Record<string, string | number> = { page, pageSize };
    if (loanContractId?.trim()) params['loanContractId'] = loanContractId.trim();
    return this.http.get<PagedApiResponse<BnplRepaymentRow>>(`${this.apiBase}/api/v1/loans/repayments`, { params });
  }

  listDelinquencyCases(): Observable<DelinquencyCaseRow[]> {
    return this.http.get<DelinquencyCaseRow[]>(`${this.apiBase}/api/v1/loans/delinquency/cases`);
  }

  listLendingCategoryInterestPolicies(): Observable<LendingCategoryInterestPolicyRow[]> {
    return this.http.get<LendingCategoryInterestPolicyRow[]>(`${this.apiBase}/api/v1/lending/governance/category-policies`);
  }

  listLendingSellerGovernanceRules(): Observable<LendingSellerGovernanceRuleRow[]> {
    return this.http.get<LendingSellerGovernanceRuleRow[]>(`${this.apiBase}/api/v1/lending/governance/seller-rules`);
  }

  evaluateDelinquency(loanContractId?: string): Observable<DelinquencyCaseRow[]> {
    const params = loanContractId?.trim() ? { loanContractId: loanContractId.trim() } : undefined;
    return this.http.post<DelinquencyCaseRow[]>(`${this.apiBase}/api/v1/loans/delinquency/evaluate`, null, { params });
  }

  quoteBnpl(payload: { storeId?: string | null; amount: number; currencyCode: string }): Observable<BnplQuoteResponse> {
    return this.http.post<BnplQuoteResponse>(`${this.apiBase}/api/v1/bnpl/quote`, payload);
  }

  listFunderProfiles(): Observable<FunderProfileRow[]> {
    return this.http.get<FunderProfileRow[]>(`${this.apiBase}/api/v1/payments/portfolio/funders`);
  }

  getFunderProfile(funderId: string): Observable<FunderProfileRow> {
    return this.http.get<FunderProfileRow>(`${this.apiBase}/api/v1/payments/portfolio/funders/${funderId}`);
  }

  createFunderProfile(payload: CreateFunderProfilePayload): Observable<FunderProfileRow> {
    return this.http.post<FunderProfileRow>(`${this.apiBase}/api/v1/payments/portfolio/funders`, payload);
  }

  updateFunderProfile(funderId: string, payload: UpdateFunderProfilePayload): Observable<FunderProfileRow> {
    return this.http.put<FunderProfileRow>(`${this.apiBase}/api/v1/payments/portfolio/funders/${funderId}`, payload);
  }

  activateFunderProfile(funderId: string): Observable<FunderProfileRow> {
    return this.http.post<FunderProfileRow>(`${this.apiBase}/api/v1/payments/portfolio/funders/${funderId}/activate`, null);
  }

  suspendFunderProfile(funderId: string): Observable<FunderProfileRow> {
    return this.http.post<FunderProfileRow>(`${this.apiBase}/api/v1/payments/portfolio/funders/${funderId}/suspend`, null);
  }

  listFundingPools(funderId?: string): Observable<FundingPoolRow[]> {
    const params = funderId?.trim() ? { funderId: funderId.trim() } : undefined;
    return this.http.get<FundingPoolRow[]>(`${this.apiBase}/api/v1/payments/portfolio/pools`, { params });
  }

  getFundingPool(poolId: string): Observable<FundingPoolRow> {
    return this.http.get<FundingPoolRow>(`${this.apiBase}/api/v1/payments/portfolio/pools/${poolId}`);
  }

  createFundingPool(payload: CreateFundingPoolPayload): Observable<FundingPoolRow> {
    return this.http.post<FundingPoolRow>(`${this.apiBase}/api/v1/payments/portfolio/pools`, payload);
  }

  activateFundingPool(poolId: string): Observable<FundingPoolRow> {
    return this.http.post<FundingPoolRow>(`${this.apiBase}/api/v1/payments/portfolio/pools/${poolId}/activate`, null);
  }

  freezeFundingPool(poolId: string): Observable<FundingPoolRow> {
    return this.http.post<FundingPoolRow>(`${this.apiBase}/api/v1/payments/portfolio/pools/${poolId}/freeze`, null);
  }

  closeFundingPool(poolId: string): Observable<FundingPoolRow> {
    return this.http.post<FundingPoolRow>(`${this.apiBase}/api/v1/payments/portfolio/pools/${poolId}/close`, null);
  }

  getPoolWaterfallPolicy(poolId: string): Observable<FundingPoolWaterfallPolicyRow | null> {
    return this.http.get<FundingPoolWaterfallPolicyRow>(`${this.apiBase}/api/v1/payments/portfolio/pools/${poolId}/waterfall`);
  }

  upsertPoolWaterfallPolicy(poolId: string, payload: UpsertFundingPoolWaterfallPolicyPayload): Observable<FundingPoolWaterfallPolicyRow> {
    return this.http.put<FundingPoolWaterfallPolicyRow>(`${this.apiBase}/api/v1/payments/portfolio/pools/${poolId}/waterfall`, payload);
  }

  listPortfolioAllocations(filters?: {
    funderId?: string;
    fundingPoolId?: string;
    loanContractId?: string;
    status?: string;
    page?: number;
    pageSize?: number;
  }): Observable<PagedApiResponse<PortfolioAllocationRow>> {
    const params: Record<string, string> = {};
    if (filters?.funderId?.trim()) params['funderId'] = filters.funderId.trim();
    if (filters?.fundingPoolId?.trim()) params['fundingPoolId'] = filters.fundingPoolId.trim();
    if (filters?.loanContractId?.trim()) params['loanContractId'] = filters.loanContractId.trim();
    if (filters?.status?.trim()) params['status'] = filters.status.trim();
    if (filters?.page) params['page'] = String(filters.page);
    if (filters?.pageSize) params['pageSize'] = String(filters.pageSize);
    return this.http.get<PagedApiResponse<PortfolioAllocationRow>>(`${this.apiBase}/api/v1/payments/portfolio/allocations`, { params });
  }

  getPortfolioAllocation(allocationId: string): Observable<PortfolioAllocationRow> {
    return this.http.get<PortfolioAllocationRow>(`${this.apiBase}/api/v1/payments/portfolio/allocations/${allocationId}`);
  }

  allocateLoanToPool(payload: AllocatePortfolioLoanPayload): Observable<PortfolioAllocationEnvelope> {
    return this.http.post<PortfolioAllocationEnvelope>(`${this.apiBase}/api/v1/payments/portfolio/allocations`, payload);
  }

  releasePortfolioAllocation(allocationId: string): Observable<PortfolioAllocationRow> {
    return this.http.post<PortfolioAllocationRow>(`${this.apiBase}/api/v1/payments/portfolio/allocations/${allocationId}/release`, null);
  }

  getFunderExposure(funderId?: string, currencyCode?: string): Observable<FunderExposureSummaryRow[]> {
    const params: Record<string, string> = {};
    if (funderId?.trim()) params['funderId'] = funderId.trim();
    if (currencyCode?.trim()) params['currencyCode'] = currencyCode.trim();
    return this.http.get<FunderExposureSummaryRow[]>(`${this.apiBase}/api/v1/payments/portfolio/exposure`, { params });
  }

  listRepaymentDistributions(filters?: {
    repaymentId?: string;
    loanContractId?: string;
    funderId?: string;
    page?: number;
    pageSize?: number;
  }): Observable<PagedApiResponse<PortfolioRepaymentDistributionRow>> {
    const params: Record<string, string> = {};
    if (filters?.repaymentId?.trim()) params['repaymentId'] = filters.repaymentId.trim();
    if (filters?.loanContractId?.trim()) params['loanContractId'] = filters.loanContractId.trim();
    if (filters?.funderId?.trim()) params['funderId'] = filters.funderId.trim();
    if (filters?.page) params['page'] = String(filters.page);
    if (filters?.pageSize) params['pageSize'] = String(filters.pageSize);
    return this.http.get<PagedApiResponse<PortfolioRepaymentDistributionRow>>(`${this.apiBase}/api/v1/payments/portfolio/repayments/distributions`, { params });
  }

  getFinancialCompletenessDashboard(): Observable<FinancialCompletenessDashboardRow> {
    return this.http.get<FinancialCompletenessDashboardRow>(`${this.apiBase}/api/v1/payments/financial-completeness/dashboard`);
  }

  getPostingReadiness(): Observable<PostingReadinessCheckRow[]> {
    return this.http.get<PostingReadinessCheckRow[]>(`${this.apiBase}/api/v1/payments/financial-completeness/posting-readiness`);
  }

  getFinancialReconciliationSummary(runId?: string): Observable<ReconciliationSummaryRow> {
    const params = runId?.trim() ? { runId: runId.trim() } : undefined;
    return this.http.get<ReconciliationSummaryRow>(`${this.apiBase}/api/v1/payments/reconciliation/summary`, { params });
  }

  startReconciliationRun(payload: StartReconciliationRunPayload): Observable<ReconciliationRunEnvelope> {
    return this.http.post<ReconciliationRunEnvelope>(`${this.apiBase}/api/v1/payments/reconciliation/runs`, payload);
  }

  listReconciliationRuns(filters?: {
    scope?: string;
    status?: string;
    page?: number;
    pageSize?: number;
  }): Observable<PagedApiResponse<ReconciliationRunRow>> {
    const params: Record<string, string> = {};
    if (filters?.scope?.trim()) params['scope'] = filters.scope.trim();
    if (filters?.status?.trim()) params['status'] = filters.status.trim();
    if (filters?.page) params['page'] = String(filters.page);
    if (filters?.pageSize) params['pageSize'] = String(filters.pageSize);
    return this.http.get<PagedApiResponse<ReconciliationRunRow>>(`${this.apiBase}/api/v1/payments/reconciliation/runs`, { params });
  }

  getReconciliationRun(runId: string): Observable<ReconciliationRunDetailRow> {
    return this.http.get<ReconciliationRunDetailRow>(`${this.apiBase}/api/v1/payments/reconciliation/runs/${runId}`);
  }

  listReconciliationFindings(filters?: {
    runId?: string;
    status?: string;
    severity?: string;
    findingType?: string;
    sourceModule?: string;
    page?: number;
    pageSize?: number;
  }): Observable<PagedApiResponse<ReconciliationFindingRow>> {
    const params: Record<string, string> = {};
    if (filters?.runId?.trim()) params['runId'] = filters.runId.trim();
    if (filters?.status?.trim()) params['status'] = filters.status.trim();
    if (filters?.severity?.trim()) params['severity'] = filters.severity.trim();
    if (filters?.findingType?.trim()) params['findingType'] = filters.findingType.trim();
    if (filters?.sourceModule?.trim()) params['sourceModule'] = filters.sourceModule.trim();
    if (filters?.page) params['page'] = String(filters.page);
    if (filters?.pageSize) params['pageSize'] = String(filters.pageSize);
    return this.http.get<PagedApiResponse<ReconciliationFindingRow>>(`${this.apiBase}/api/v1/payments/reconciliation/findings`, { params });
  }

  acknowledgeReconciliationFinding(findingId: string): Observable<ReconciliationFindingRow> {
    return this.http.post<ReconciliationFindingRow>(`${this.apiBase}/api/v1/payments/reconciliation/findings/${findingId}/acknowledge`, null);
  }

  resolveReconciliationFinding(findingId: string, evidenceJson: string): Observable<ReconciliationFindingRow> {
    return this.http.post<ReconciliationFindingRow>(`${this.apiBase}/api/v1/payments/reconciliation/findings/${findingId}/resolve`, { evidenceJson });
  }

  getInvestorPayableSummary(): Observable<InvestorPayableSummaryRow> {
    return this.http.get<InvestorPayableSummaryRow>(`${this.apiBase}/api/v1/payments/investor-payables/summary`);
  }

  listInvestorPayables(filters?: {
    funderId?: string;
    fundingPoolId?: string;
    status?: string;
    currencyCode?: string;
    page?: number;
    pageSize?: number;
  }): Observable<PagedApiResponse<InvestorPayableRow>> {
    const params: Record<string, string> = {};
    if (filters?.funderId?.trim()) params['funderId'] = filters.funderId.trim();
    if (filters?.fundingPoolId?.trim()) params['fundingPoolId'] = filters.fundingPoolId.trim();
    if (filters?.status?.trim()) params['status'] = filters.status.trim();
    if (filters?.currencyCode?.trim()) params['currencyCode'] = filters.currencyCode.trim();
    if (filters?.page) params['page'] = String(filters.page);
    if (filters?.pageSize) params['pageSize'] = String(filters.pageSize);
    return this.http.get<PagedApiResponse<InvestorPayableRow>>(`${this.apiBase}/api/v1/payments/investor-payables`, { params });
  }

  getInvestorPayable(payableId: string): Observable<InvestorPayableRow> {
    return this.http.get<InvestorPayableRow>(`${this.apiBase}/api/v1/payments/investor-payables/${payableId}`);
  }

  approveInvestorPayable(payableId: string): Observable<InvestorPayableRow> {
    return this.http.post<InvestorPayableRow>(`${this.apiBase}/api/v1/payments/investor-payables/${payableId}/approve`, null);
  }

  markInvestorPayablePaid(payableId: string, payload: MarkInvestorPayablePaidPayload): Observable<InvestorPayableRow> {
    return this.http.post<InvestorPayableRow>(`${this.apiBase}/api/v1/payments/investor-payables/${payableId}/mark-paid`, payload);
  }

  getRepaymentDistribution(distributionId: string): Observable<PortfolioRepaymentDistributionRow> {
    return this.http.get<PortfolioRepaymentDistributionRow>(`${this.apiBase}/api/v1/payments/portfolio/repayments/distributions/${distributionId}`);
  }
}

export interface LendingCategoryInterestPolicyRow {
  id: string;
  policyCode: string;
  name: string;
  categoryId: string;
  currencyCode: string | null;
  interestRatePercent: number;
  feeAmount: number | null;
  minEligibleAmount: number | null;
  maxEligibleAmount: number | null;
  priority: number;
  isActive: boolean;
  effectiveFromUtc: string;
  effectiveUntilUtc: string | null;
}

export interface LendingSellerGovernanceRuleRow {
  id: string;
  ruleCode: string;
  name: string;
  sellerId: string;
  bnplMode: string;
  interestRatePercentOverride: number | null;
  feeAmountOverride: number | null;
  minOrderAmount: number | null;
  maxOrderAmount: number | null;
  payoutHoldDaysAfterBnplOrder: number;
  blockPayoutUntilFirstInstallmentPaid: boolean;
  blockPayoutWhenLoanDelinquent: boolean;
  priority: number;
  isActive: boolean;
  effectiveFromUtc: string;
  effectiveUntilUtc: string | null;
}

export interface BnplPlanRow {
  id: string;
  name: string;
  storeId: string | null;
  currencyCode: string;
  minAmount: number;
  maxAmount: number;
  installmentCount: number;
  interestRatePercent: number;
  feeAmount: number;
  downPaymentPercent: number;
  isActive: boolean;
  policyJson: string;
}

export interface CreateBnplPlanPayload {
  name: string;
  storeId?: string | null;
  currencyCode: string;
  minAmount: number;
  maxAmount: number;
  installmentCount: number;
  interestRatePercent: number;
  feeAmount: number;
  downPaymentPercent: number;
  isActive: boolean;
  policyJson?: string;
}

export interface SubmitCreditApplicationPayload {
  customerAccountId: string;
  partyId?: string | null;
  storeId?: string | null;
  sourceModule: string;
  sourceEntityName: string;
  sourceEntityId: string;
  requestedAmount: number;
  currencyCode: string;
  purpose: string;
  requestedInstallments: number;
  monthlyIncome: number;
  existingDebt: number;
  idempotencyKey: string;
  idempotencyPayloadHash: string;
  correlationId?: string | null;
}

export interface BnplCommitPayload {
  creditApplicationId: string;
  planId: string;
  sourceOrderId?: string | null;
  checkoutSessionId?: string | null;
  idempotencyKey: string;
  idempotencyPayloadHash: string;
}

export interface BnplCommitResponse {
  loanContract: LoanContractRow;
  installments: LoanInstallmentRow[];
  isReplay: boolean;
}

export interface ActivateLoanContractPayload {
  idempotencyKey: string;
  idempotencyPayloadHash: string;
  postingProfile: {
    bookId: string;
    periodId: string;
    debitAccountId: string;
    creditAccountId: string;
    currencyCode: string;
  };
}

export interface CreditApplicationRow {
  id: string;
  customerAccountId: string;
  partyId: string | null;
  storeId: string | null;
  requestedAmount: number;
  currencyCode: string;
  purpose: string;
  status: string;
  idempotencyKey: string;
  idempotencyPayloadHash: string;
  createdAtUtc: string;
  submittedAtUtc: string | null;
  decidedAtUtc: string | null;
}

export interface CreditDecisionRow {
  id: string;
  applicationId: string;
  decision: string;
  approvedAmount: number;
  maxInstallments: number;
  interestRatePercent: number;
  feeAmount: number;
  reasonCodesJson: string;
  policyVersion: string;
  score: number | null;
  confidence: number | null;
  decidedByUserId: string | null;
  decidedAtUtc: string;
}

export interface CreditDecisionPayload {
  decision: string;
  approvedAmount: number;
  maxInstallments: number;
  interestRatePercent: number;
  feeAmount: number;
  reasonCodesJson?: string;
}

export interface CreditLineRow {
  id: string;
  customerAccountId: string;
  approvedLimit: number;
  usedAmount: number;
  availableAmount: number;
  currencyCode: string;
  status: string;
  validFromUtc: string;
  validToUtc: string | null;
  sourceDecisionId: string;
}

export interface LoanContractRow {
  id: string;
  creditApplicationId: string;
  creditLineId: string | null;
  customerAccountId: string;
  sourceOrderId: string | null;
  checkoutSessionId: string | null;
  principalAmount: number;
  feeAmount: number;
  interestAmount: number;
  totalPayable: number;
  currencyCode: string;
  status: string;
  idempotencyKey: string;
  idempotencyPayloadHash: string;
  activatedAtUtc: string | null;
}

export interface LoanInstallmentRow {
  id: string;
  loanContractId: string;
  sequence: number;
  dueDateUtc: string;
  principalAmount: number;
  interestAmount: number;
  feeAmount: number;
  amountDue: number;
  amountPaid: number;
  status: string;
}

export interface BnplRepaymentRow {
  id: string;
  loanContractId: string;
  amount: number;
  unallocatedAmount: number;
  currencyCode: string;
  sourcePaymentId: string | null;
  allocationJson: string;
  idempotencyKey: string;
  idempotencyPayloadHash: string;
  receivedAtUtc: string;
  journalEntryId: string | null;
  postingErrorCode: string | null;
}

export interface DelinquencyCaseRow {
  id: string;
  loanContractId: string;
  overdueInstallmentCount: number;
  overdueAmount: number;
  firstOverdueAtUtc: string;
  status: string;
  resolvedAtUtc: string | null;
}

export interface BnplQuoteResponse {
  plans: BnplQuotePlanRow[];
}

export interface BnplQuotePlanRow {
  planId: string;
  planName: string;
  installmentCount: number;
  principalAmount: number;
  interestAmount: number;
  feeAmount: number;
  totalPayable: number;
  schedulePreview: { sequence: number; dueDateUtc: string; amountDue: number }[];
}

export interface FunderProfileRow {
  id: string;
  partyId: string | null;
  customerAccountId: string | null;
  funderCode: string;
  displayName: string;
  status: string;
  riskAppetiteJson: string;
  complianceReference: string;
  payoutProfileReference: string | null;
}

export interface CreateFunderProfilePayload {
  partyId?: string | null;
  customerAccountId?: string | null;
  funderCode: string;
  displayName: string;
  riskAppetiteJson?: string;
  complianceReference: string;
  payoutProfileReference?: string | null;
}

export interface UpdateFunderProfilePayload {
  partyId?: string | null;
  customerAccountId?: string | null;
  displayName: string;
  riskAppetiteJson?: string;
  complianceReference: string;
  payoutProfileReference?: string | null;
}

export interface FundingPoolRow {
  id: string;
  funderId: string;
  poolCode: string;
  currencyCode: string;
  committedCapital: number;
  availableCapital: number;
  allocatedCapital: number;
  repaidCapital: number;
  status: string;
  allocationPolicyJson: string;
  validFromUtc: string;
  validToUtc: string | null;
}

export interface CreateFundingPoolPayload {
  funderId: string;
  poolCode: string;
  currencyCode: string;
  committedCapital: number;
  allocationPolicyJson?: string;
  validFromUtc: string;
  validToUtc?: string | null;
  idempotencyKey?: string | null;
  correlationId?: string | null;
}

export interface FundingPoolWaterfallPolicyRow {
  id: string;
  fundingPoolId: string;
  policyType: string;
  policyJson: string;
  isActive: boolean;
  effectiveFromUtc: string;
  effectiveToUtc: string | null;
}

export interface UpsertFundingPoolWaterfallPolicyPayload {
  policyType: string;
  policyJson: string;
  effectiveFromUtc: string;
  effectiveToUtc?: string | null;
}

export interface PortfolioAllocationRow {
  id: string;
  fundingPoolId: string;
  funderId: string;
  loanContractId: string;
  creditApplicationId: string | null;
  customerAccountId: string;
  principalAllocated: number;
  currencyCode: string;
  allocationPercent: number;
  status: string;
  idempotencyKey: string;
  idempotencyPayloadHash: string;
  correlationId: string | null;
  allocatedAtUtc: string;
}

export interface AllocatePortfolioLoanPayload {
  fundingPoolId: string;
  loanContractId: string;
  creditApplicationId?: string | null;
  customerAccountId: string;
  principalAllocated: number;
  currencyCode: string;
  allocationPercent: number;
  idempotencyKey: string;
  idempotencyPayloadHash: string;
  correlationId?: string | null;
  postFinancialEntry?: boolean;
  postingProfile?: {
    bookId: string;
    periodId: string;
    debitAccountId: string;
    creditAccountId: string;
    currencyCode: string;
  };
}

export interface PortfolioAllocationEnvelope {
  allocation: PortfolioAllocationRow;
  isReplay: boolean;
}

export interface FunderExposureSummaryRow {
  funderId: string;
  currencyCode: string;
  totalAllocated: number;
  outstandingPrincipal: number;
  repaidPrincipal: number;
  delinquentExposure: number;
  defaultedExposure: number;
  asOfUtc: string;
}

export interface PortfolioRepaymentDistributionRow {
  id: string;
  repaymentId: string;
  loanContractId: string;
  portfolioAllocationId: string;
  fundingPoolId: string;
  funderId: string;
  amount: number;
  currencyCode: string;
  idempotencyKey: string;
  idempotencyPayloadHash: string;
  correlationId: string | null;
  distributedAtUtc: string;
}

export interface ReconciliationSummaryRow {
  openFindings: number;
  acknowledgedFindings: number;
  resolvedFindings: number;
  criticalFindings: number;
  findingsByType: Record<string, number>;
}

export interface InvestorPayableSummaryRow {
  pendingCount: number;
  approvedCount: number;
  paidCount: number;
  cancelledCount: number;
  pendingAmount: number;
  approvedAmount: number;
  paidAmount: number;
}

export interface FinancialCompletenessDashboardRow {
  reconciliation: ReconciliationSummaryRow;
  investorPayables: InvestorPayableSummaryRow;
  failedReconciliationRuns: number;
  completedReconciliationRuns: number;
  legacyParallelFlowFindings: number;
  legacyPaymentsLendingParallelActive: boolean;
}

export interface PostingReadinessCheckRow {
  flowKey: string;
  status: string;
  detailKey: string | null;
}

export interface StartReconciliationRunPayload {
  scope: string;
  periodFromUtc: string;
  periodToUtc: string;
  idempotencyKey: string;
  idempotencyPayloadHash: string;
  correlationId?: string | null;
}

export interface ReconciliationRunRow {
  id: string;
  scope: string;
  periodFromUtc: string;
  periodToUtc: string;
  status: string;
  startedAtUtc: string | null;
  completedAtUtc: string | null;
  correlationId: string;
  triggeredByUserId: string | null;
  idempotencyKey: string;
  failureReason: string | null;
}

export interface ReconciliationRunEnvelope {
  run: ReconciliationRunRow;
  findingCount: number;
}

export interface ReconciliationRunDetailRow {
  run: ReconciliationRunRow;
  findings: ReconciliationFindingRow[];
}

export interface ReconciliationFindingRow {
  id: string;
  runId: string;
  severity: string;
  findingType: string;
  sourceModule: string;
  sourceEntityName: string;
  sourceEntityId: string;
  expectedAmount: number | null;
  actualAmount: number | null;
  currencyCode: string;
  status: string;
  evidenceJson: string;
  createdAtUtc: string;
  acknowledgedAtUtc: string | null;
  resolvedAtUtc: string | null;
  resolvedEvidenceJson: string | null;
}

export interface InvestorPayableRow {
  id: string;
  funderId: string;
  fundingPoolId: string | null;
  sourceRepaymentDistributionId: string;
  amount: number;
  currencyCode: string;
  status: string;
  idempotencyKey: string;
  idempotencyPayloadHash: string;
  correlationId: string | null;
  journalEntryId: string | null;
  postingErrorCode: string | null;
  createdAtUtc: string;
  paidAtUtc: string | null;
}

export interface MarkInvestorPayablePaidPayload {
  idempotencyKey: string;
  postingProfile: {
    bookId: string;
    periodId: string;
    debitAccountId: string;
    creditAccountId: string;
    currencyCode: string;
  };
}
